import React from 'react';
import { safeJSONParse } from '@/utils/helper';

interface CallScore {
	value: number;
	description: string;
}

interface CallSentiment {
	positive: number;
	neutral: number;
	negative: number;
	description: string;
}

interface CallAnalyticsProps {
	callSummary?: string;
	callScore?: string;
	callSentiments?: string;
}

const CallAnalytics: React.FC<CallAnalyticsProps> = ({
	callSummary,
	callScore,
	callSentiments,
}) => {
	const parseCallScore = (scoreString?: string): CallScore | null => {
		if (!scoreString) {
			return null;
		}
		return safeJSONParse(scoreString, null);
	};

	const parseCallSentiment = (sentimentString?: string): CallSentiment | null => {
		if (!sentimentString) {
			return null;
		}
		return safeJSONParse(sentimentString, null);
	};

	const score = parseCallScore(callScore);
	const sentiment = parseCallSentiment(callSentiments);

	return (
		<div className="space-y-5">
			{/* Call Summary */}
			{callSummary && (
				<div>
					<h4 className="font-semibold text-gray-900 mb-3 text-sm tracking-wide">SUMMARY</h4>
					<div className="bg-gray-50 p-4 rounded-lg border">
						<p className="text-sm text-gray-700 leading-relaxed">{callSummary}</p>
					</div>
				</div>
			)}

			{/* Call Score */}
			{score && (
				<div>
					<h4 className="font-semibold text-gray-900 mb-3 text-sm tracking-wide">CALL SCORE</h4>
					<div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
						<div className="flex items-center gap-3 mb-2">
							<span className="text-2xl font-bold text-blue-600">{score.value}/5</span>
							<div className="flex">
								{[1, 2, 3, 4, 5].map((star) => (
									<div
										key={star}
										className={`w-4 h-4 ${star <= score.value ? 'text-yellow-400' : 'text-gray-300'}`}
									>
										★
									</div>
								))}
							</div>
						</div>
						<p className="text-sm text-gray-700">{score.description}</p>
					</div>
				</div>
			)}

			{/* Call Sentiments */}
			{sentiment && (
				<div>
					<h4 className="font-semibold text-gray-900 mb-3 text-sm tracking-wide">
						SENTIMENT ANALYSIS
					</h4>
					<div className="bg-green-50 p-4 rounded-lg border">
						<div className="grid grid-cols-3 gap-4 mb-3">
							<div className="text-center">
								<div className="text-lg font-bold text-green-600">{sentiment.positive}%</div>
								<div className="text-xs text-gray-600">Positive</div>
							</div>
							<div className="text-center">
								<div className="text-lg font-bold text-gray-600">{sentiment.neutral}%</div>
								<div className="text-xs text-gray-600">Neutral</div>
							</div>
							<div className="text-center">
								<div className="text-lg font-bold text-red-600">{sentiment.negative}%</div>
								<div className="text-xs text-gray-600">Negative</div>
							</div>
						</div>
						<p className="text-sm text-gray-700">{sentiment.description}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default CallAnalytics;
